// Orchestrator for the staff exhibit create/edit flow. Replaces the legacy
// 765-LOC src/pages/staff/ExhibitForm.js. PR 11 ST 8 wired the composition;
// ST 10 migrated upload-image / generate-qr / regenerate-code onto typed
// wrappers + React Query hooks. PR 13 deleted the auto-translate handler
// (and the underlying /exhibits/translate endpoint).
//
// Option (B) per project_pr11_cadence: CRA's App.js continues to serve
// /staff/exhibit/*; only the import path flips to point here.

import { useEffect, useRef, useState, type FC, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import { assetUrl } from "@shared/api/assets";
import { extractErrorMessage } from "@shared/api/errors";
import type {
  Exhibit,
  ExhibitCreate,
  ExhibitUpdate,
} from "@shared/api/exhibits";
import {
  useAssignExhibitZone,
  useCreateExhibit,
  useExhibit,
  useGenerateExhibitQR,
  useRegenerateExhibitCode,
  useUpdateExhibit,
  useUploadExhibitImage,
} from "@shared/api/hooks/useExhibits";
import { useZoneList } from "@shared/api/hooks/useZones";
import { useExhibitions } from "@shared/api/hooks/useExhibitions";
import { useAuth } from "@shared/contexts/AuthContext";

import CodeSection from "@shared/components/CodeSection";
import Translations from "@staff/features/shared/Translations";
import ImageUpload from "@staff/features/shared/ImageUpload";
import LockdownBanner from "@staff/features/shared/LockdownBanner";
import { useEntityForm } from "@staff/features/shared/useEntityForm";
import ExhibitMediaPanel from "@staff/features/exhibits/ExhibitMediaPanel";
import QRSection from "@staff/features/shared/QRSection";
import PublishToggle from "@staff/features/shared/PublishToggle";
import ZoneSelect from "@staff/features/exhibits/ZoneSelect";

interface FormState {
  titleTc: string;
  titleEn: string;
  titleSc: string;
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
}

const EMPTY_FORM: FormState = {
  titleTc: "",
  titleEn: "",
  titleSc: "",
  descriptionTc: "",
  descriptionEn: "",
  descriptionSc: "",
};

const ExhibitForm: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // Staged cover removal (2026-09-08): applied by Save via `removeCoverImage`.
  const [removeCover, setRemoveCover] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [exhibitCode, setExhibitCode] = useState<string | null>(null);

  const { data: exhibit } = useExhibit(id);
  const createExhibit = useCreateExhibit();
  const updateExhibit = useUpdateExhibit();
  const uploadImage = useUploadExhibitImage();
  const generateQR = useGenerateExhibitQR();
  const regenerateCode = useRegenerateExhibitCode();

  // Born-in-a-zone placement. Exhibits are reverse-linked from the zone
  // side; the current zone comes from the exhibit list annotation (#4) and
  // the selectable options from the scope-filtered staff zone list — an
  // editor's dropdown can only contain zones they may target. Editors must
  // pick one (backend 422s otherwise); admin may leave it unassigned.
  const { isAdmin } = useAuth();
  const assignZone = useAssignExhibitZone();
  // F-R (2026-09-08): the staff single read carries the holding zone, so the
  // form no longer downloads the whole exhibit list to find it.
  const listed = exhibit;
  const { data: allZones = [] } = useZoneList();
  const { data: placementExhibitions = [] } = useExhibitions({
    includeArchived: true,
  });
  const zoneOptions = allZones.map((z) => {
    const exhibitionTitle = placementExhibitions.find(
      (ex) => ex.id === z.exhibitionId,
    )?.titleTc;
    return {
      id: z.id,
      label: exhibitionTitle ? `${z.titleTc} — ${exhibitionTitle}` : z.titleTc,
    };
  });
  const originalZoneId = listed?.zoneId ?? "";
  const [zoneId, setZoneId] = useState("");
  // Seed once from the list annotation (same init-once pattern as
  // useEntityForm) so a background refetch never clobbers an unsaved pick.
  const zoneSeededRef = useRef(false);
  useEffect(() => {
    if (isEdit && !zoneSeededRef.current && listed) {
      zoneSeededRef.current = true;
      setZoneId(listed.zoneId ?? "");
    }
  }, [isEdit, listed]);

  // F-C: exhibits cascade-archived / cascade-purged by exhibition-level
  // operations. The shared engine owns the seed-once guard, lock derivation,
  // code-locked reason, unsaved-changes diff, and the per-language narration
  // nudge (see useEntityForm). Only the auxiliary state is exhibit-specific.
  const {
    formData,
    patch,
    isPurged,
    isLocked,
    codeLockedReason,
    hasUnsavedChanges,
    narrationDescriptionChanged,
  } = useEntityForm<Exhibit, FormState>({
    entity: exhibit,
    isEdit,
    entityNoun: "exhibit",
    emptyForm: EMPTY_FORM,
    seedForm: (ex) => ({
      titleTc: ex.titleTc ?? "",
      titleEn: ex.titleEn ?? "",
      titleSc: ex.titleSc ?? "",
      descriptionTc: ex.descriptionTc ?? "",
      descriptionEn: ex.descriptionEn ?? "",
      descriptionSc: ex.descriptionSc ?? "",
    }),
    onSeed: (ex) => {
      if (ex.coverImageId) setImagePreview(assetUrl(ex.coverImageId));
      if (ex.qrCodeUrl) setQrCodeUrl(ex.qrCodeUrl);
      if (ex.code) setExhibitCode(ex.code);
    },
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      let savedId = id;
      if (isEdit && savedId) {
        // Skip the text PUT when only the image / zone changed (2026-07-27,
        // approved) — it would be a no-op update with an empty audit diff.
        if (hasUnsavedChanges || removeCover) {
          await updateExhibit.mutateAsync({
            exhibitId: savedId,
            payload: {
              ...formData,
              ...(removeCover ? { removeCoverImage: true } : {}),
            } as ExhibitUpdate,
          });
          if (removeCover) {
            setRemoveCover(false);
            setImagePreview(null);
          }
        }
        if (zoneId !== originalZoneId) {
          await assignZone.mutateAsync({
            exhibitId: savedId,
            zoneId: zoneId || null,
          });
        }
        toast.success("Exhibit updated");
      } else {
        if (!isAdmin && !zoneId) {
          toast.error(
            "Select a zone — exhibits must live in a zone you manage",
          );
          return;
        }
        const created = await createExhibit.mutateAsync({
          ...(formData as ExhibitCreate),
          zoneId: zoneId || null,
        });
        savedId = created.id;
        setExhibitCode(created.code);
        toast.success("Exhibit created");
      }
      if (imageFile && savedId) {
        await uploadImage.mutateAsync({ exhibitId: savedId, file: imageFile });
        setImageFile(null);
      }
      // Update stays on the form (2026-07-24 UX request) — the update
      // mutation primes the detail cache, so the form re-syncs in place.
      // Create still returns to the exhibit list.
      if (!isEdit) {
        navigate("/staff/dashboard");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save exhibit"));
    }
  };

  const handleFileSelected = (file: File) => {
    setRemoveCover(false); // a new pick supersedes a staged removal
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFileTooLarge = () => toast.error("Image must be less than 10MB");

  const handleGenerateQR = async () => {
    if (!id) {
      toast.error("Please save the exhibit first");
      return;
    }
    try {
      const res = await generateQR.mutateAsync(id);
      setQrCodeUrl(res.qrCodeUrl);
      toast.success("QR code generated");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to generate QR code"));
    }
  };

  const handleRegenerateCode = async () => {
    if (!id) {
      toast.error("Please save the exhibit first");
      return;
    }
    if (
      !window.confirm(
        "Regenerate exhibit code? This will change the code visitors use to access this exhibit.",
      )
    ) {
      return;
    }
    try {
      const res = await regenerateCode.mutateAsync(id);
      setExhibitCode(res.code);
      toast.success(`New code generated: ${res.code}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to regenerate code"));
    }
  };

  const saving =
    createExhibit.isPending || updateExhibit.isPending || assignZone.isPending;

  // Option (a), 2026-07-27: Update is disabled when nothing changed. Dirty =
  // text fields (engine diff) OR a newly picked cover image OR a zone move —
  // the engine only tracks text, so the other two are checked here.
  const dirty =
    hasUnsavedChanges ||
    imageFile !== null ||
    removeCover ||
    (isEdit && zoneId !== originalZoneId);

  return (
    <div
      className="min-h-screen bg-background pb-12"
      data-testid="exhibit-form-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/staff/dashboard")}
              variant="ghost"
              size="sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-heading font-bold">
              {isEdit ? "Edit Exhibit" : "Create New Exhibit"}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {isLocked && (
            <LockdownBanner
              isPurged={isPurged}
              purged={
                <>
                  This exhibit has been <strong>purged</strong> via its parent
                  exhibition. Tombstone only; cover-image bytes removed.
                </>
              }
              archived={
                <>
                  This exhibit is <strong>archived</strong> (cascaded by its
                  parent exhibition). Restore the parent exhibition to bring it
                  back before editing.
                </>
              }
            />
          )}
          {!isLocked && (
            <ImageUpload
              label="Exhibit Image"
              previewUrl={imagePreview}
              onFileSelected={handleFileSelected}
              onFileTooLarge={handleFileTooLarge}
              onRemove={
                isEdit && exhibit?.coverImageId && !imageFile
                  ? () => setRemoveCover(true)
                  : undefined
              }
              removalPending={removeCover}
              onUndoRemove={() => setRemoveCover(false)}
            />
          )}
          {isLocked && imagePreview && (
            <div className="space-y-2">
              <img
                src={imagePreview}
                alt="Cover"
                className="w-full max-w-md h-64 object-cover rounded-lg border border-border"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          )}

          <Translations
            formData={formData}
            onChange={patch}
            disabled={isLocked}
          />

          {/* Media sits right after title & description (2026-07-24 UX
              request) — the narration text is authored from those fields. */}
          {isEdit && id && exhibit && (
            <ExhibitMediaPanel
              exhibit={exhibit}
              disabled={isLocked}
              unsavedChanges={hasUnsavedChanges}
              descriptionChanged={narrationDescriptionChanged}
            />
          )}

          <div
            className="space-y-4 p-6 bg-white border border-border rounded-lg"
            data-testid="placement-section"
          >
            <h3 className="font-heading font-semibold">Placement</h3>
            <ZoneSelect
              options={zoneOptions}
              value={zoneId}
              onChange={setZoneId}
              allowNone={isAdmin}
              disabled={isLocked}
            />
            {isEdit && (
              <CodeSection
                code={exhibitCode}
                label="Exhibit"
                entityNoun="exhibit"
                onRegenerate={handleRegenerateCode}
                regenerateDisabledReason={codeLockedReason}
              />
            )}
          </div>

          {isEdit && id && (
            <>
              <QRSection
                ownerNoun="exhibit"
                entityId={id}
                qrCodeUrl={qrCodeUrl}
                onGenerate={handleGenerateQR}
                generating={generateQR.isPending}
              />
              <PublishToggle ownerKind="exhibit" entityId={id} />
            </>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={
                saving ||
                isLocked ||
                (!isAdmin && !zoneId) ||
                (isEdit && !dirty)
              }
              title={isEdit && !dirty ? "No changes to save" : undefined}
              className="px-8"
              data-testid="save-exhibit-button"
            >
              {saving
                ? "Saving..."
                : isEdit
                  ? "Update Exhibit"
                  : "Create Exhibit"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/staff/dashboard")}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExhibitForm;
